import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Panel } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, Check } from 'lucide-react';

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [info, setInfo] = useState(null); // { email, name, is_new_user }
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setVerifying(false); setError('Missing reset token in link.'); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        setInfo(data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'This link is invalid or has expired.');
      } finally { setVerifying(false); }
    })();
  }, [token]);

  const rules = [
    { key: 'len', label: 'At least 8 characters', ok: password.length >= 8 },
    { key: 'match', label: 'Passwords match', ok: password.length > 0 && password === confirmPassword },
  ];
  const canSubmit = rules.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) { toast.error('Please meet the password requirements'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/auth/set-password', { token, password });
      setSession(data.token, data.user);
      toast.success('Password set. Welcome to Heritage.');
      // Route by role: founders to their console, otherwise members to home.
      if (data.user?.role === 'founder') navigate('/console/founder');
      else if (data.user?.role === 'admin') navigate('/admin');
      else navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set password.');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <HeritageSeal size={72} />
          <div className="h-eyebrow mt-3">Heritage Apparel</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
            {info?.is_new_user ? 'Activate your account' : 'Set a new password'}
          </div>
        </div>

        <Panel className="p-8">
          {verifying && <div className="h-eyebrow text-center">Verifying link...</div>}

          {!verifying && error && (
            <div className="text-center" data-testid="set-password-error">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto"
                   style={{ background: 'rgba(154,44,44,0.1)', color: '#9A2C2C' }}>
                <Lock size={22} />
              </div>
              <div className="h-serif text-xl mt-3" style={{ color: 'var(--heritage-plum)' }}>Link problem</div>
              <div className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>{error}</div>
              <Link to="/login" className="h-btn-outline mt-5 inline-flex text-xs">Back to login</Link>
            </div>
          )}

          {!verifying && !error && info && (
            <form onSubmit={submit} className="space-y-4">
              <div className="text-sm text-center" style={{ color: 'var(--heritage-plum)' }}>
                Kia ora {info.name || ''}, choose a password for<br />
                <span className="h-serif">{info.email}</span>
              </div>

              <div>
                <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>New password</label>
                <div className="relative">
                  <input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-md pl-3 pr-10 py-2 text-sm"
                    style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }}
                    autoFocus data-testid="set-password-input" />
                  <button type="button" onClick={() => setShow(!show)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100"
                    style={{ color: 'var(--heritage-plum)' }} aria-label="toggle password">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>Confirm password</label>
                <input type={show ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-md px-3 py-2 text-sm"
                  style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }}
                  data-testid="set-password-confirm" />
              </div>

              <ul className="space-y-1 text-xs" style={{ color: 'var(--heritage-plum)' }}>
                {rules.map((r) => (
                  <li key={r.key} className="flex items-center gap-2" style={{ opacity: r.ok ? 1 : 0.6 }}>
                    <span className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            background: r.ok ? 'var(--heritage-plum)' : 'transparent',
                            border: '1.5px solid var(--heritage-plum)',
                          }}>
                      {r.ok && <Check size={10} style={{ color: 'var(--heritage-cream)' }} />}
                    </span>
                    <span>{r.label}</span>
                  </li>
                ))}
              </ul>

              <button type="submit" disabled={!canSubmit || saving}
                className="h-btn w-full justify-center disabled:opacity-50"
                data-testid="set-password-submit">
                {saving ? 'Saving...' : (info.is_new_user ? 'Activate my account' : 'Set my password')}
              </button>

              <div className="text-center pt-3">
                <Link to="/login" className="text-xs opacity-70 hover:opacity-100" style={{ color: 'var(--heritage-plum)' }}>
                  Back to login
                </Link>
              </div>

              <div className="pt-3 border-t flex items-center justify-center gap-2" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <DahliaMark size={14} />
                <span className="text-[10px] h-eyebrow" style={{ color: 'var(--heritage-plum)' }}>Heritage Apparel</span>
              </div>
            </form>
          )}
        </Panel>
      </div>
    </div>
  );
}
